import { useState } from 'react';
import { Button, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import styles from './FreightRateCard.module.css';
import { RateSelector } from './RateSelector';
import { FreightCarrierDetailsModal } from '../../../freight-rate-card/freight-carrier-details-modal/FreightCarrierDetailsModal';
import { freightRateCardMessages } from '../../../freight-rate-card/freight-rate-card-messages';
import { getCarrierInitials } from '../../../freight-rate-card/get-carrier-initials';
import { getCarrierLogoSrc } from '../../../freight-rate-card/get-carrier-logo-src';

import type { LtlQuote } from '../../../../types/ltl';
import type { CarrierRateRow } from '../../../freight-rate-card/freight-rate-card-types';

type FreightRateCardProps = {
  carrierRow: CarrierRateRow;
  selectedOfferId?: string;
  onSelectRate: (quote: LtlQuote) => void;
  sellerFreightProviderId?: number;
  zIndex?: number;
};

export const FreightRateCard = ({ carrierRow, selectedOfferId, onSelectRate, sellerFreightProviderId, zIndex }: FreightRateCardProps) => {
  const intl = useIntl();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { carrierKey, carrierName, carrierScac, standard, guaranteed } = carrierRow;
  const logoSrc = getCarrierLogoSrc(carrierScac);

  const logo = logoSrc ? (
    <img alt={carrierName} className={styles.logoImage} src={logoSrc} />
  ) : (
    <span className={styles.logoInitials}>{getCarrierInitials(carrierName)}</span>
  );

  return (
    <div className={styles.card} data-testid={`freight-rate-card-${carrierKey}`}>
      <div className={styles.header}>
        <div className={styles.logoBox}>{logo}</div>
        <div className={styles.carrierBlock}>
          <Text className={styles.carrierName} size='md'>
            {carrierName}
          </Text>
          <Button h='auto' onClick={() => setIsDetailsOpen(true)} px={0} variant='hyperlink'>
            {intl.formatMessage(freightRateCardMessages.viewDetails)}
          </Button>
        </div>
      </div>
      <div className={styles.selectors}>
        <RateSelector
          isSelected={!!standard && standard.offerId === selectedOfferId}
          label={intl.formatMessage(freightRateCardMessages.standardLabel)}
          onSelect={onSelectRate}
          quote={standard}
        />
        <RateSelector
          isSelected={!!guaranteed && guaranteed.offerId === selectedOfferId}
          label={intl.formatMessage(freightRateCardMessages.guaranteedLabel)}
          onSelect={onSelectRate}
          quote={guaranteed}
        />
      </div>
      <FreightCarrierDetailsModal
        carrierName={carrierName}
        carrierScac={carrierScac}
        onClose={() => setIsDetailsOpen(false)}
        opened={isDetailsOpen}
        quote={standard ?? guaranteed!}
        sellerFreightProviderId={sellerFreightProviderId}
        zIndex={zIndex}
      />
    </div>
  );
};
